import { useLayoutEffect, useRef, useState } from "react";
import { Flex, Stack, Text } from "@mantine/core";
import type { GuestNetworkIndustryStat } from "../../hooks/useGuestNetworkStats";
import { GUEST_NETWORK_STATS_EMPTY, GUEST_NETWORK_STATS_UNAVAILABLE } from "../../hooks/useGuestNetworkStats";
import { useDiscoverHeroDonutMetrics, type DiscoverHeroStatLayout } from "../../hooks/useDiscoverHeroDonutMetrics";

const NAVY = "#04305c";
const PX_PER_SECOND = 15;

type DiscoverHeroIndustryBarsProps = {
  industries: GuestNetworkIndustryStat[];
  loadError?: boolean;
  layout?: DiscoverHeroStatLayout;
};

function IndustryRow({ row, textSize }: { row: GuestNetworkIndustryStat; textSize: "xs" | "sm" }) {
  return (
    <Text size={textSize} c={NAVY} lh={1.35}>
      {row.label}
    </Text>
  );
}

export default function DiscoverHeroIndustryBars({
  industries,
  loadError = false,
  layout = "row",
}: DiscoverHeroIndustryBarsProps) {
  const donut = useDiscoverHeroDonutMetrics();
  const visibleIndustries = industries.filter((row) => row.count > 0);
  const isColumn = layout === "column";

  if (loadError || visibleIndustries.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="md" ta="center">
        {loadError ? GUEST_NETWORK_STATS_UNAVAILABLE : GUEST_NETWORK_STATS_EMPTY}
      </Text>
    );
  }

  if (isColumn) {
    return (
      <DiscoverHeroIndustryBarsMarquee
        industries={visibleIndustries}
        textSize={donut.legendSize}
        stacked
      />
    );
  }

  return <DiscoverHeroIndustryBarsMarquee industries={visibleIndustries} textSize={donut.legendSize} />;
}

function DiscoverHeroIndustryBarsMarquee({
  industries,
  textSize,
  stacked = false,
}: {
  industries: GuestNetworkIndustryStat[];
  textSize: "xs" | "sm";
  stacked?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [marquee, setMarquee] = useState(false);
  const [durationSec, setDurationSec] = useState(24);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || industries.length === 0) return;

    const measure = () => {
      const copies = marquee ? 2 : 1;
      const oneListHeight = track.scrollHeight / copies;
      const overflows = oneListHeight > viewport.clientHeight + 2;
      setMarquee(overflows);
      if (overflows && oneListHeight > 0) {
        setDurationSec(Math.max(12, Math.round(oneListHeight / PX_PER_SECOND)));
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(track);
    return () => observer.disconnect();
  }, [industries, marquee]);

  const viewportClassName = stacked
    ? "discover-hero-industry-viewport discover-hero-industry-viewport--stacked"
    : "discover-hero-industry-viewport";

  const list = (
    <>
      <div
        ref={viewportRef}
        className={viewportClassName}
        title="Hover to pause"
      >
        <div
          ref={trackRef}
          className={`discover-hero-industry-track${marquee ? " discover-hero-industry-track--marquee" : ""}`}
          style={{ ["--industry-marquee-duration" as string]: `${durationSec}s` }}
        >
          <div className="discover-hero-industry-copy">
            {industries.map((row) => (
              <IndustryRow key={row.key} row={row} textSize={textSize} />
            ))}
          </div>
          {marquee ? (
            <div className="discover-hero-industry-copy" aria-hidden="true">
              {industries.map((row) => (
                <IndustryRow key={`dup-${row.key}`} row={row} textSize={textSize} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <Text size={textSize} c="#94A3B8" ta="center" style={{ flexShrink: 0 }}>
        Companies per industry
      </Text>
    </>
  );

  if (stacked) {
    return (
      <Stack gap={4}>
        {list}
      </Stack>
    );
  }

  return (
    <Flex direction="column" h="100%" mih={0} gap={4}>
      {list}
    </Flex>
  );
}
